import { SENTENCE_END } from '../../constants';
import { firstLineLeftOf, mapBlockSequences, paragraphTextOf, withBlanksFolded } from '../draft/blocks';
import type {
  ClauseDraft,
  DraftBlock,
  DraftList,
  DraftListItem,
  DraftNumbering,
  DraftParagraph,
  DraftRun,
} from '../draft/types';

const TEXT_COLUMN_TOLERANCE_PT = 4.5;
const CAPITALISED_START = /^\p{Lu}/u;

interface OpenList {
  list: DraftList;
  item: DraftListItem;
  listKey: string;
  level: number;
}

const textColumnOf = ({ item }: OpenList) => {
  const [lead] = item.blocks;
  return lead.kind === 'paragraph' ? lead.indent.left : undefined;
};

const listOf = ({ marker }: DraftNumbering): DraftList => ({
  kind: 'list',
  marker:
    marker.format === 'bullet'
      ? marker
      : { format: marker.format, punctuation: marker.punctuation, start: marker.ordinal },
  items: [],
});

const itemOf = (paragraph: DraftParagraph, { marker }: DraftNumbering): DraftListItem => ({
  markerBold: marker.format !== 'bullet' && marker.bold,
  blocks: [paragraph],
});

// counters(item, '.') at the top level would print 1., 2. in place of the author's 3.1, 3.2
const withTypedPath = (paragraph: DraftParagraph, { legalPath, marker }: DraftNumbering): DraftParagraph => {
  const label: DraftRun = {
    kind: 'text',
    text: `${legalPath} `,
    format: { ...paragraph.runs[0].format, bold: marker.format !== 'bullet' && marker.bold },
  };
  return { ...paragraph, numbering: undefined, runs: [label, ...paragraph.runs] };
};

const continuesItem = (lead: DraftParagraph, paragraph: DraftParagraph, text: string, afterBlank: boolean) => {
  const { left, hanging = 0 } = paragraph.indent;
  if (lead.indent.left === undefined || left === undefined) return false;

  const sameTextColumn = Math.abs(left - lead.indent.left) <= TEXT_COLUMN_TOLERANCE_PT;
  const hung = hanging > 0;
  const startsCapitalised = CAPITALISED_START.test(text);
  const leadEndsSentence = SENTENCE_END.test(paragraphTextOf(lead));
  const blankBetween = afterBlank && !paragraph.headingStyled;
  return sameTextColumn && !hung && !startsCapitalised && !leadEndsSentence && !blankBetween;
};

const joined = (target: DraftParagraph, { runs, spaceAfter }: DraftParagraph): DraftParagraph => {
  const lastText = target.runs.findLastIndex((run) => run.kind === 'text');
  return {
    ...target,
    spaceAfter,
    runs: [
      ...target.runs.map((run, index) =>
        index === lastText && run.kind === 'text' ? { ...run, text: `${run.text} ` } : run,
      ),
      ...runs,
    ],
  };
};

const assemble = (blocks: DraftBlock[]): DraftBlock[] => {
  const out: DraftBlock[] = [];
  const open: OpenList[] = [];
  let blanks: DraftParagraph[] = [];
  let awaiting: DraftListItem | undefined;
  // blank lines inside a list widen the gap under the last block of the deepest open item
  const foldBlanks = () => {
    const { blocks } = open[open.length - 1].item;
    const last = blocks[blocks.length - 1];
    if (last.kind === 'paragraph') blocks[blocks.length - 1] = withBlanksFolded(last, blanks);
  };

  const hostOf = (column: number | undefined) =>
    column === undefined
      ? -1
      : open.findLastIndex((entry) => {
          const textColumn = textColumnOf(entry);
          return textColumn !== undefined && column >= textColumn - TEXT_COLUMN_TOLERANCE_PT;
        });

  const placeOutside = (block: DraftBlock) => {
    open.length = 0;
    awaiting = undefined;
    out.push(...blanks, block);
    blanks = [];
  };

  const placeItem = (paragraph: DraftParagraph, numbering: DraftNumbering) => {
    const sameList = open.findLastIndex(
      ({ listKey, level }) => listKey === numbering.listKey && level <= numbering.level,
    );
    const parent = sameList >= 0 ? sameList : hostOf(firstLineLeftOf(paragraph));
    if (parent < 0 && numbering.legalPath) {
      placeOutside(withTypedPath(paragraph, numbering));
      return;
    }

    if (!open.length) out.push(...blanks);
    else foldBlanks();
    blanks = [];

    const item = itemOf(paragraph, numbering);
    awaiting = item;

    if (sameList >= 0 && open[sameList].level === numbering.level) {
      open.length = sameList + 1;
      open[sameList].list.items.push(item);
      open[sameList].item = item;
      return;
    }

    open.length = parent + 1;
    const list = listOf(numbering);
    list.items.push(item);
    if (parent >= 0) {
      open[parent].item.blocks.push(list);
    } else {
      out.push(list);
    }
    open.push({ list, item, listKey: numbering.listKey, level: numbering.level });
  };

  const placeParagraph = (paragraph: DraftParagraph) => {
    const text = paragraphTextOf(paragraph);
    if (!text) {
      blanks.push(paragraph);
      return;
    }
    const lead = awaiting?.blocks[0];
    const openItem = awaiting;
    awaiting = undefined;
    if (openItem && lead?.kind === 'paragraph' && continuesItem(lead, paragraph, text, blanks.length > 0)) {
      openItem.blocks = [joined(lead, paragraph)];
      return;
    }

    const host = paragraph.headingStyled ? -1 : hostOf(paragraph.indent.left);
    if (host < 0) {
      placeOutside(paragraph);
      return;
    }
    open.length = host + 1;
    open[host].item.blocks.push(...blanks, paragraph);
    blanks = [];
  };

  blocks.forEach((block) => {
    if (block.kind !== 'paragraph') {
      placeOutside(block);
    } else if (block.numbering) {
      placeItem(block, block.numbering);
    } else {
      placeParagraph(block);
    }
  });
  out.push(...blanks);
  return out;
};

export const assembleLists = (draft: ClauseDraft): ClauseDraft => ({
  ...draft,
  blocks: mapBlockSequences(draft.blocks, assemble),
});
